'use client';

import React, { useState, useCallback, useMemo, useRef } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountOrders } from 'src/hooks/query/accountTradingData/useOrders';
import { calculateMaxPositionSize } from 'src/utils/position';
import { notify } from 'src/utils/notifications';
import Button from 'src/components/primitives/Button/Button';
import { cj, cm } from 'src/utils/classNames';
import {
	UIMarket,
	getMaxLeverageForMarket,
	validateLeverageChange as validateLeverageChange_dep,
	convertLeverageToMarginRatio,
} from '@velocity-exchange/common';
import {
	BASE_PRECISION_EXP,
	BigNum,
	VelocityClient,
	MarketType,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import Slider from 'src/components/primitives/Slider/Slider';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { Add, Subtract } from '@velocity-exchange/icons';
import useModal from './hooks/useModal';
import ResponsiveModal from './ResponsiveModal/ResponsiveModal';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { createLeverageMarks } from 'src/components/primitives/Slider/utils';
import useSliderConvertValue from '../primitives/Slider/hooks/useSliderConvertValue';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import Tag from 'src/components/primitives/Tag/Tag';
import Input from 'src/components/primitives/Input/Input';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import InputUi from 'src/components/primitives/Input/InputUi';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import { useIsolatedPositionMargin } from 'src/hooks/positions/useIsolatedPositionMargin';
import { useUpdateUserMarketMaxLeverage } from 'src/hooks/trade/edit/useUpdateUserMarketMaxLeverage';
import useEffectiveMarginMode from 'src/hooks/marginMode/useEffectiveMarginMode';

const MIN_LEVERAGE = 1;

export interface PositionMaxLeverageModalProps {
	marketIndex: number;
}

export default function PositionMaxLeverageModal({
	marketIndex,
}: PositionMaxLeverageModalProps) {
	const { hide, push } = useModal();
	const isWalletConnected = useWalletIsConnected();
	const driftClient = useDriftStore((s) => s.driftClient.client);

	const currentAccount = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']
	);
	const userAccountClient = currentAccount?.client;
	const { maxLeverage: marketMaxLeverage } = getMaxLeverageForMarket(
		MarketType.PERP,
		marketIndex,
		driftClient as VelocityClient
	);
	const allMarketsInfo = useMarketsInfoStore((s) => s.allMarketsInfo);
	const marketInfo = allMarketsInfo.perpLookup[marketIndex ?? 0];
	const userAccountHasMaxMarginRatio =
		!!userAccountClient?.getUserAccount()?.maxMarginRatio;

	const updateMaxLeverageForMarket = useMarketSettingsStore(
		(s) => s.setMaxLeverageForMarket
	);

	const marketIdKey = useMemo(
		() => UIMarket.createPerpMarket(marketIndex).marketId.key,
		[marketIndex]
	);
	const { mode: marginMode } = useEffectiveMarginMode(marketIdKey);

	// Sync leverage and funds breakdown
	const openPerpPositions = useCurrentAccountPositions();
	const openOrders = useCurrentAccountOrders();
	const currentPosition = openPerpPositions?.find(
		(p) => p.marketIndex === (marketIndex ?? 0)
	);

	const resolvedInitialLev = useGetDisplayLeverageForMarket(marketIndex);

	const [leverageInput, setLeverageInput] = useState(
		formatLeverage(resolvedInitialLev)
	);
	const leverageInputNum = parseFloat(leverageInput);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isRemoveLimit = leverageInput.trim() === '' || leverageInputNum === 0;

	const { updateUserMarketMaxLeverage } = useUpdateUserMarketMaxLeverage();

	const validateInput = useCallback(
		(value: string) => {
			if (!value) {
				return true;
			}

			if (value.endsWith('.')) {
				return false;
			}

			const leverageNum = parseInt(value);

			return leverageNum <= marketMaxLeverage;
		},
		[marketMaxLeverage]
	);

	const handleInputChange = useCallback(
		(val: string) => {
			if (!validateInput(val)) {
				return;
			}

			setLeverageInput(!val ? '' : formatLeverage(parseInt(val)));
		},
		[validateInput]
	);

	const [_showLimitAlert] = useState(false);

	// Function to validate if new leverage would exceed initial free collateral available
	const validateLeverageChange = useCallback(
		(newLeverage: number): boolean => {
			if (!userAccountClient || marketIndex === undefined) return true;

			return validateLeverageChange_dep({
				user: userAccountClient,
				marketIndex,
				newLeverage,
			});
		},
		[userAccountClient, marketIndex]
	);

	const leverageTooLow = !validateLeverageChange(parseFloat(leverageInput));

	/* Isolated margin guard: Block lowering leverage unless isolated margin covers new Initial requirement */
	const isolatedMargin = useIsolatedPositionMargin(marketIndex);
	const isolatedDeposit = useMemo(() => {
		if (!driftClient?.hasUser()) return ZERO;
		try {
			return (
				driftClient.getIsolatedPerpPositionTokenAmount(marketIndex) ?? ZERO
			);
		} catch (e: unknown) {
			console.error('Error getting isolated position token amount', e);
			return ZERO;
		}
	}, [driftClient, marketIndex, openPerpPositions]);
	const hasIsolatedDeposit = useMemo(
		() => isolatedDeposit.gt(ZERO),
		[isolatedDeposit]
	);
	const proposedInitialRequirement = useMemo(() => {
		if (!userAccountClient || marketIndex === undefined) return undefined;
		const perpPosition = userAccountClient.getPerpPosition(marketIndex);
		const inputNum = parseFloat(leverageInput);
		const newMarginRatio = convertLeverageToMarginRatio(inputNum);
		if (!perpPosition || !newMarginRatio) return undefined;
		const modifiedPosition = {
			...perpPosition,
			maxMarginRatio: newMarginRatio,
		};
		const health = userAccountClient.getPerpPositionHealth({
			marginCategory: 'Initial',
			perpPosition: modifiedPosition,
		});
		return BigNum.from(health.weightedValue, QUOTE_PRECISION_EXP);
	}, [userAccountClient, marketIndex, leverageInput]);
	const isolatedInsufficientForLowering = useMemo(() => {
		if (!hasIsolatedDeposit) return false;
		if (!proposedInitialRequirement) return false;
		return proposedInitialRequirement.gt(isolatedMargin);
	}, [hasIsolatedDeposit, proposedInitialRequirement, isolatedMargin]);

	const isInvalid =
		parseFloat(leverageInput) === 0 ||
		parseFloat(leverageInput) > marketMaxLeverage ||
		leverageTooLow ||
		isolatedInsufficientForLowering;

	const handleSliderChange = (sliderPercent: number) => {
		const leverageValue = convertValue({
			value: sliderPercent,
			from: 'percent',
		});

		setLeverageInput(formatLeverage(clampLeverage(leverageValue)));
	};

	const onConfirm = async (close: () => void) => {
		if (marketIndex === undefined) return;

		setIsSubmitting(true);
		try {
			const inputNum = parseFloat(leverageInput);

			// Determine if user has an open position in this market
			const hasOpenPosition = !!openPerpPositions?.some(
				(p) => p.marketIndex === marketIndex && !p.baseSize.eq(ZERO)
			);
			const hasOpenOrder = !!openOrders?.some(
				(o) => o.marketIndex === marketIndex
			);
			const marketSymbol =
				UIMarket.createPerpMarket(marketIndex).uiSymbols.marketSymbol;

			const newLeverage = isRemoveLimit ? 0 : inputNum;

			if (hasOpenPosition || hasOpenOrder) {
				const toastId = `update_market_max_leverage_${marketIndex}_${Date.now()}`;
				const didUpdate = await updateUserMarketMaxLeverage(
					toastId,
					marketIndex,
					newLeverage
				);

				if (didUpdate) {
					updateMaxLeverageForMarket(marketSymbol, newLeverage);
					close();
				}
			} else {
				// Save locally without a transaction
				updateMaxLeverageForMarket(marketSymbol, newLeverage);
				close();
			}
		} catch (e: any) {
			// Reuse generic handler used elsewhere if preferred, but keep simple here
			notify({
				type: 'error',
				message: e?.message ?? 'Transaction failed',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const marks = useMemo(
		() => createLeverageMarks(marketMaxLeverage),
		[marketMaxLeverage]
	);

	const convertValue = useSliderConvertValue({ marks });

	const maxPositionSize = useMemo(() => {
		return calculateMaxPositionSize(leverageInputNum, marketInfo?.account);
	}, [leverageInputNum, marketInfo?.marketId?.key]);

	const clampLeverage = useCallback(
		(desiredLev: number) => {
			const clamped = Math.min(
				marketMaxLeverage,
				Math.max(MIN_LEVERAGE, desiredLev)
			);

			return clamped;
		},
		[marketMaxLeverage]
	);

	const handleLeverageStepClick = useCallback(
		(direction: 'decrement' | 'increment') => {
			const raw =
				(leverageInputNum || 0) + (direction === 'increment' ? 1 : -1);

			const next = clampLeverage(raw);
			setLeverageInput(formatLeverage(next));
		},
		[leverageInputNum, clampLeverage]
	);

	const sliderValue = useMemo(() => {
		if (isNaN(leverageInputNum)) {
			return 0;
		}

		return convertValue({
			value: leverageInputNum,
			from: 'leverage',
		});
	}, [leverageInputNum, marks]);

	const currentPositionTooLarge = useMemo(() => {
		return (
			currentPosition &&
			!currentPosition.baseSize.eq(ZERO) &&
			maxPositionSize.lt(currentPosition.baseSize)
		);
	}, [currentPosition, maxPositionSize]);

	const inputRef = useRef<HTMLInputElement>(null);

	if (marketIndex === undefined) {
		return null;
	}

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="position-max-leverage-modal"
		>
			<ResponsiveModal.Title
				closeButtonTestId="position-max-leverage-modal-close"
				bottomElement={
					<Tag variant="neutral" className="w-fit">
						{marginMode === 'isolated' ? 'Isolated' : 'Cross'}
					</Tag>
				}
			>
				Adjust{' '}
				{UIMarket.createPerpMarket(marketIndex).uiSymbols.marketDisplaySymbol}{' '}
				Leverage
			</ResponsiveModal.Title>

			<ResponsiveModal.Body>
				<div className="flex flex-col gap-y-md">
					<InputUi.Container>
						<InputUi.Prefix size="md">
							<IconButton
								size="lg"
								variant="tertiary"
								onClick={() => handleLeverageStepClick('decrement')}
								icon={Subtract}
							/>
						</InputUi.Prefix>
						<InputUi.Value
							size="md"
							className="text-center typo-label-md"
							onClick={() => inputRef.current?.focus()}
						>
							<Input
								type="number"
								variant="headless"
								inputClassName={cj(
									'placeholder:text-text-secondary',
									CSS.supports('field-sizing', 'content')
										? 'field-sizing-content'
										: '-ml-4 w-8 text-right'
								)}
								value={leverageInput}
								onChange={handleInputChange}
								validate={validateInput}
								placeholder="1"
								ref={inputRef}
							/>
							<span
								className={cj(
									'select-none',
									!leverageInput && 'text-text-secondary'
								)}
							>
								x
							</span>
						</InputUi.Value>
						<InputUi.Suffix size="md">
							<IconButton
								size="lg"
								variant="tertiary"
								onClick={() => handleLeverageStepClick('increment')}
								icon={Add}
							/>
						</InputUi.Suffix>
					</InputUi.Container>

					<Slider
						id="position-max-leverage-slider"
						marks={marks}
						value={sliderValue}
						onValueChange={handleSliderChange}
						onValueCommit={handleSliderChange}
						noValueInTooltip
						step={0.1}
					/>
				</div>

				<p
					className={cm(
						currentPositionTooLarge && 'text-text-negative-red-button'
					)}
				>
					Individual max position size at leverage:{' '}
					{BigNum.from(maxPositionSize, BASE_PRECISION_EXP).prettyPrint(true)}{' '}
					{
						UIMarket.createPerpMarket(marketIndex).uiSymbols
							.baseAssetDisplaySymbol
					}
				</p>

				<p>
					Changing the leverage will also affect all open positions and orders
					in the same market.
				</p>

				<p
					className={cj(
						'transition-colors',
						leverageInputNum > 20 ? 'text-star-yellow' : 'text-text-secondary'
					)}
				>
					High leverage increases your liquidation risk. Manage your risk
					carefully.
				</p>

				{userAccountHasMaxMarginRatio && (
					<p className="text-text-negative-red-button">
						For per-market leverage to take effect properly, set Account Max
						Leverage to [No Limit] in your{' '}
						<TextButton
							size="sm"
							variant="tertiary"
							onClick={() =>
								push({ type: 'settings', props: { view: 'margin' } })
							}
						>
							margin settings
						</TextButton>
						.
					</p>
				)}

				<p
					className={cj(
						'text-text-negative-red-button',
						'opacity-0 transition-opacity select-none',
						leverageTooLow && 'opacity-100 select-auto'
					)}
				>
					New leverage is too low to cover the current position size.
				</p>
				<p
					className={cj(
						'text-text-negative-red-button',
						'opacity-0 transition-opacity select-none',
						isolatedInsufficientForLowering && 'opacity-100 select-auto'
					)}
				>
					Insufficient isolated margin to reduce leverage. Add more margin
					first.
				</p>
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				{({ close }) => (
					<Button
						size="lg"
						className="w-full"
						onClick={() => {
							if (!isWalletConnected) {
								push({ type: 'connect_wallet', props: {} });
								return;
							}
							onConfirm(close);
						}}
						disabled={
							isWalletConnected && (isInvalid || currentPositionTooLarge)
						}
						isLoading={isSubmitting}
						dataTestId="position-max-leverage-modal-submit"
					>
						{isWalletConnected ? 'Confirm' : 'Connect Wallet'}
					</Button>
				)}
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}

function formatLeverage(value: number) {
	if (!value || value <= 0 || Number.isNaN(value)) {
		return '1';
	}

	return value.toFixed(0);
}
